import { EmeraldIcon } from "@emerald-react/icon";
import { getIconData } from "@shared/utils/map";
import type { PropertyFieldRule } from "@survey-types/survey.core.types";
import type { Property } from "@survey-types/survey.property.types";
import classNames from "classnames";

interface PDFPropertyHeaderProps {
  readonly property: Property;
  readonly index: number;
  readonly showCurrentLocationIcon?: boolean;
  readonly className?: string;
  /** Field rules from surveyPropertyFieldRule.propertyFieldRuleList — used to check if name/address1 are toggled on */
  readonly fieldRules?: PropertyFieldRule[];
  /** When false, address2 is a standard field shown below address1 and alongside city/state/zip */
  readonly hasAddressLine2Custom?: boolean;
}

/**
 * PDF Property Header Component
 * Reusable header for property-related PDF pages with property info, address, and numbering
 *
 * @example
 * <PDFPropertyHeader
 *   property={property}
 *   index={0}
 *   showCurrentLocationIcon={true}
 * />
 */
const PDFPropertyHeader = ({
  property,
  index,
  showCurrentLocationIcon = false,
  className = "",
  fieldRules,
  hasAddressLine2Custom = true,
}: PDFPropertyHeaderProps) => {
  const isCurrentLocation =
    showCurrentLocationIcon && property.isCurrentLocation;

  // A field is visible when no rule exists for it OR the rule has isHidden: false
  const isFieldVisible = (key: string): boolean => {
    if (!fieldRules?.length) return true;
    const rule = fieldRules.find((r) => r.fieldKey === key);
    return rule ? !rule.isHidden : true;
  };

  // Show name as title when:
  // 1. The "name" field is not toggled off
  // 2. The property has actual name data (not empty / "Untitled Property")
  const nameHasData = !!property.name && property.name !== "Untitled Property";
  const showNameAsTitle = isFieldVisible("name") && nameHasData;

  // Title: property name when available and visible, otherwise address1 (always shown once)
  const displayTitle = showNameAsTitle
    ? property.name
    : property.address1 || "—";

  const stateZip =
    [
      isFieldVisible("stateProvince") ? property.stateProvince : null,
      isFieldVisible("postalCode") ? property.postalCode : null,
    ]
      .filter(Boolean)
      .join(" ") || null;

  const showAddress2Line = !hasAddressLine2Custom && isFieldVisible("address2");

  // Split into 2 rows only when address2 is visible and not custom:
  //   1) address1
  //   2) address2, city, state zip
  // Otherwise keep a single line with address1, city, state zip.
  const address1Line = showAddress2Line
    ? showNameAsTitle && isFieldVisible("address1")
      ? property.address1
      : null
    : null;

  const address2CityStateZipLine = showAddress2Line
    ? [
        isFieldVisible("address2")
          ? property.address2 === "—"
            ? ""
            : property.address2
          : null,
        isFieldVisible("city") ? property.city : null,
        stateZip,
      ]
        .filter(Boolean)
        .join(", ") || null
    : null;

  const addressLine = !showAddress2Line
    ? (() => {
        const parts = showNameAsTitle
          ? [
              isFieldVisible("address1") ? property.address1 : null,
              isFieldVisible("city") ? property.city : null,
              stateZip,
            ]
          : [isFieldVisible("city") ? property.city : null, stateZip];
        return parts.filter(Boolean).join(", ") || null;
      })()
    : null;

  return (
    <header
      className={classNames(
        "flex w-full bg-dark-secondary py-6 px-8 gap-x-2.5",
        className,
      )}
    >
      {isCurrentLocation ? (
        <CurrentLocationIcon property={property} />
      ) : (
        <OrderNumber index={index} />
      )}

      <div className="border-l border-secondary" />

      <div className="text-primary">
        <h2 className="font-heading property-header">{displayTitle}</h2>
        {showAddress2Line ? (
          <>
            {address1Line && <p className="text-sm">{address1Line}</p>}
            {address2CityStateZipLine && (
              <p className="text-sm">{address2CityStateZipLine}</p>
            )}
          </>
        ) : (
          addressLine && <p className="text-sm">{addressLine}</p>
        )}
      </div>
    </header>
  );
};

const CurrentLocationIcon = ({ property }: { readonly property: Property }) => {
  const iconData = getIconData(property);
  return (
    <div className="mt-2 flex size-6 items-center justify-center rounded-full bg-white">
      <EmeraldIcon
        icon="stars"
        type="filled"
        variant="icon"
        className="text-xs"
        color={iconData.iconColor}
      />
    </div>
  );
};

const OrderNumber = ({ index }: { readonly index: number }) => (
  <div className="flex items-start">
    <span className="text-2xl text-secondary">{index + 1}</span>
  </div>
);

export default PDFPropertyHeader;
